/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import { Theme, makeStyles, Dialog, DialogContent, DialogActions, DialogTitle, Slider, Zoom } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { DropzoneAreaBase, FileObject } from 'material-ui-dropzone';
import Cropper from 'react-easy-crop';
import { Area, Point, Size } from 'react-easy-crop/types';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import Button from './Button';
import { FileUploadResult, Shape } from '../types/types';
import { StyledText } from './StyledText';
import { useAlert } from '../contexts';

interface DropzoneStyleProps {
  hideDropzone: boolean
}

const useDropzoneStyles = makeStyles<Theme, DropzoneStyleProps>((theme: Theme) => ({
  root: {
    margin: 0,
    padding: 10,
    '& .MuiTypography-h5': {
      fontFamily: theme.typography.fontFamily,
      fontSize: 16,
      fontWeight: 300,
      color: '#5C657C'
    },
    '& .MuiDropzoneArea-textContainer > svg': {
      margin: 'auto',
      width: 154,
      height: 41,
      backgroundImage: 'url(/browse-file.svg)',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'contain'
    },
    '& .MuiDropzoneArea-textContainer > svg > path': {
      display: 'none'
    },
    '& .MuiGrid-spacing-xs-8 > .MuiGrid-item': {
      padding: 0
    }
  }
}));

const getMimeTypeFromImageSrc = (imageSrc:string) => (imageSrc.split(';')[0].split(':')[1]);

const getImgExtension = (mime:string) => {
  const imgExt = mime.split('/')[1];
  if (imgExt === 'jpeg' || imgExt === 'png' || imgExt === 'jpg') {
    return imgExt;
  } else {
    return '';
  }
};

const createImage = async (url:string):Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

const scaleImageByHeight = async (imageSrc: string, scaledHeight: number): Promise<FileUploadResult> => {
  const image = await createImage(imageSrc);
  // scaleFactor >= 1 means enlarge image and scaleFactor < 1 means shrink image
  const scaleFactor = scaledHeight / image.naturalHeight;
  const scaledWidth = image.naturalWidth * scaleFactor;
  const canvas = document.createElement('canvas');
  canvas.width = scaledWidth;
  canvas.height = scaledHeight;
  const ctx = canvas.getContext('2d');
  const mimeType = getMimeTypeFromImageSrc(imageSrc);
  const imgExt = getImgExtension(mimeType);

  if (ctx) {
    ctx.drawImage(
      image,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight,
      0,
      0,
      scaledWidth,
      scaledHeight
    );
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob:any) => {
      if (imgExt) {
        blob.name = `newVRImage.${imgExt}`;
      } else {
        reject(new Error('couldn\'t read data'));
      }
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const data = reader.result;
        if (data) {
          resolve({ data, url: URL.createObjectURL(blob), mime: mimeType });
        } else {
          reject(new Error('couldn\'t read data'));
        }
      };
    }, mimeType, 1);
  });
};

const cropImage = async (imageSrc:string, crop: any):Promise<FileUploadResult> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext('2d');
  const mimeType = getMimeTypeFromImageSrc(imageSrc);
  const imgExt = getImgExtension(mimeType);

  if (ctx) {
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob:any) => {
      if (imgExt) {
        blob.name = `newVRImage.${imgExt}`;
      } else {
        reject(new Error('couldn\'t read data'));
      }
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const data = reader.result;
        if (data) {
          resolve({ data, url: URL.createObjectURL(blob), mime: mimeType });
        } else {
          reject(new Error('couldn\'t read data'));
        }
      };
    }, mimeType, 1);
  });
};

const resizeLargeImage = async (imageSrc:string):Promise<FileUploadResult> => {
  const image = await createImage(imageSrc);
  let maxHeight = 3000;
  let maxWidth = 3000;
  if (image.height > image.width) {
    maxWidth = Math.floor(maxHeight * (image.width / image.height));
  } else {
    maxHeight = Math.floor(maxWidth * (image.height / image.width));
  }
  image.height = maxHeight;
  image.width = maxWidth;

  const canvas = document.createElement('canvas');
  canvas.width = maxWidth;
  canvas.height = maxHeight;
  const ctx = canvas.getContext('2d');
  const mimeType = getMimeTypeFromImageSrc(imageSrc);
  const imgExt = getImgExtension(mimeType);

  if (ctx) {
    ctx.drawImage(image, 0, 0, maxWidth, maxHeight);
  }
  if (!ctx || !mimeType || !imgExt) {
    throw new Error('couldn\'t read data');
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob:any) => {
      if (imgExt) {
        blob.name = `newVRImage.${imgExt}`;
      } else {
        reject(new Error('couldn\'t read data'));
      }
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const data = reader.result;
        if (data) {
          resolve({ data, url: URL.createObjectURL(blob), mime: mimeType });
        } else {
          reject(new Error('couldn\'t read data'));
        }
      };
    }, mimeType, 1);
  });
};

interface ImageUploadStyleProps {
  size: number
  hideDropzone: boolean
}

const useStyles = makeStyles<Theme, ImageUploadStyleProps>((theme: Theme) => ({
  container: {
    width: 'auto',
    height: 'auto',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    margin: '0'
  },
  panel: {
    width: 'auto',
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',

    '& .MuiDropzoneArea-root': {
      display: (p) => (p.hideDropzone ? 'none' : 'block')
    }
  }
}));

interface ImageUploadProps {
  size?: number,
  isOpen: boolean,
  handleClose: () => void,
  onFinished: (resultUrl: Promise<FileUploadResult>) => void,
  shape?: Shape,
  dimen?: { width: number, height:number },
  skipCrop?: boolean
}

interface ImageCropperDefaultState {
  src: string,
  crop: any,
  zoom: number,
  croppedPixels: any | null
  files: FileObject[]
}

const defaults:ImageCropperDefaultState = {
  src: '',
  crop: { x: 0, y: 0 },
  zoom: 1,
  croppedPixels: null,
  files: []
};

const cropWindowSize = 250;

const containerProps: CSSProperties = {
  width: `${cropWindowSize}px`,
  height: `${cropWindowSize}px`,
  position: 'relative'
};

const cropperStyles = {
  containerStyle: containerProps
};

const ImageUploadDialog: React.FC<ImageUploadProps> = ({
  size = 185,
  isOpen,
  handleClose,
  onFinished,
  shape = Shape.Round,
  skipCrop = false,
  dimen
}) => {
  const maxZoom = 3;
  const [src, setSrc] = useState(defaults.src);
  const [crop, setCrop] = useState<any>(defaults.crop);
  const [zoom, setZoom] = useState(defaults.zoom);
  const [croppedPixels, setCroppedPixels] = useState<Area>();
  const [minZoom, setMinZoom] = useState(defaults.zoom);
  const [files, setFiles] = useState<FileObject[]>([]);
  const styles = useStyles({ size, hideDropzone: files.length > 0 });
  const { postNewAlert } = useAlert();


  const handleAdd = (fileObjects: FileObject[]) => {
    const file: any = fileObjects[0];
    if (file) {
      if (skipCrop) {
        setFiles([file]);
      } else {
        setSrc(file.data || '');
      }
    }
  };

  const handleDelete = () => {
    setFiles([]);
  };

  const handleCropChange = (newCrop: any) => {
    setCrop(newCrop);
  };

  const handleZoomChange = (newZoom: number) => {
    // let slider handle zooming
  };

  const handleCropComplete = async (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedPixels(croppedAreaPixels);
  };

  const onMediaLoaded = async (mediaObject: any) => {
    if (shape === Shape.Round || !dimen) {
      const minDimen = Math.min(mediaObject.width, mediaObject.height);
      const maxDimen = Math.max(mediaObject.naturalWidth, mediaObject.naturalHeight);
      if (minDimen < size) {
        setZoom(size / minDimen);
        setMinZoom(size / minDimen);
      } else {
        setMinZoom(size / cropWindowSize);
      }
      if (maxDimen > 3000) {
        try {
          const result = await resizeLargeImage(src);
          const resizedImg = result.data;
          if (resizedImg) {
            const newSrc = resizedImg as string;
            setSrc(newSrc || '');
          }
        } catch (e: unknown) {
          setSrc('');
          postNewAlert('An error occurred while resizing the profile picture.  Please try again later.');
          console.error('An error occurred while resizing the profile picture.  Please try again later.');
        }
      }
    } else if ((mediaObject.width < dimen.width) || (mediaObject.height < dimen.height)) {
      const newZoom = Math.max(dimen.width / mediaObject.width, dimen.height / mediaObject.height);
      setZoom(newZoom);
      setMinZoom(newZoom);
    }
  };

  const handleSubmit = () => {
    if (skipCrop) {
      onFinished(scaleImageByHeight(files[0].data as string, 100));
    } else {
      onFinished(cropImage(src, croppedPixels));
    }
  };

  const onZoomSliderChange = (event: object, value: number | number[]) => {
    if (typeof (value) === 'number') {
      setZoom(value);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSrc(defaults.src);
      setCrop(defaults.crop);
      setZoom(defaults.zoom);
      setCroppedPixels(defaults.croppedPixels);
      setFiles(defaults.files);
    }
  }, [isOpen]);

  const submitDisabld = (skipCrop && !files.length) || (!skipCrop && !src);
  const dropzoneClasses = useDropzoneStyles({ hideDropzone: !skipCrop || (skipCrop && files.length > 0) });

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
    >
      <DialogTitle disableTypography>
        <StyledText variant='h2' color='textPrimary'>Upload Image</StyledText>
      </DialogTitle>
      <DialogContent>
        <Box className={styles.container}>
          <Box className={styles.panel}>
            {src
              ? (
                <>
                  <Cropper
                    onCropChange={handleCropChange}
                    onZoomChange={handleZoomChange}
                    onCropComplete={handleCropComplete}
                    zoomWithScroll={false}
                    crop={crop}
                    zoom={zoom}
                    showGrid={false}
                    style={cropperStyles}
                    onMediaLoaded={onMediaLoaded}
                    image={src}
                    cropSize={shape === Shape.Rect && dimen ? dimen : { width: size, height: size }}
                    cropShape={shape === Shape.Round ? 'round' : 'rect'}
                  />
                  <Slider
                    color='secondary'
                    value={zoom}
                    min={minZoom}
                    max={maxZoom}
                    step={0.01}
                    onChange={onZoomSliderChange}
                  />
                </>
              ) : (
                <Box className={styles.panel}>
                  <DropzoneAreaBase
                    classes={dropzoneClasses}
                    fileObjects={files}
                    dropzoneText='Drag & Drop an image here or'
                    filesLimit={1}
                    onDelete={handleDelete}
                    onAdd={handleAdd}
                    acceptedFiles={['image/jpeg', 'image/png', 'image/jpg']}
                    showPreviews
                    showPreviewsInDropzone={false}
                    previewText=''
                    previewGridProps={{
                      container: {
                        spacing: 0
                      },
                      item: {
                        xs: 12
                      }
                    }}
                  />
                </Box>
              )}

          </Box>
        </Box>
      </DialogContent>
      <DialogActions style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          onClick={handleClose}
          type='button'
          variant='text'
          color='primary'
          size='large'
        >
          Cancel
        </Button>
        <Button
          type='submit'
          variant='contained'
          disableElevation
          color='primary'
          size='large'
          disabled={submitDisabld}
          onClick={handleSubmit}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageUploadDialog;
